import React, { useState, useRef, useEffect } from "react";
import Navbar from "../../../components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  Store,
  Globe,
  CheckCircle2,
  Clock,
  Search,
  ArrowUpDown,
  MoreVertical,
  Tag,
  Users,
  BarChart3,
  Filter,
  ChevronDown,
  Trash2,
  X,
  Key,
  Calendar,
  User,
} from "lucide-react";
import { toast } from "react-toastify";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import moment from "moment";
import { debounce } from "lodash";
import { createPortal } from "react-dom";
import { campaignsRequest } from "../../../requests/vendor/CampaignsRequest";

const CampaignRedemptions = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedDeal, setSelectedDeal] = useState("all");
  const dropdownRef = useRef(null);
  const [filterSearchTerm, setFilterSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [redemptionData, setRedemptionData] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  //* Statistics
  const [totalRedemptions, setTotalRedemptions] = useState(0);
  const [totalPendingRedemptions, setTotalPendingRedemptions] = useState(0);
  const [totalCompletedRedemptions, setTotalCompletedRedemptions] = useState(0);
  const [totalRedemptionCompletionRate, setTotalRedemptionCompletionRate] =
    useState(0);
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  //* Fetch Redemptions
  const fetchRedemptions = async () => {
    setIsLoading(true);
    try {
      const response = await campaignsRequest.getCampaignRedemptions();
      if (
        response?.data?.status_code === "000" &&
        !response?.data?.in_error
      ) {
        const data = response?.data?.data;
        const completedCount = data.filter(
          (item) => item.status === "completed"
        ).length;
        const totalCount = data.length;
        const completionRate =
          totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

        setRedemptionData(data);
        setTotalRedemptions(totalCount);
        setTotalPendingRedemptions(
          data.filter((item) => item.status === "pending").length
        );
        setTotalCompletedRedemptions(completedCount);
        setTotalRedemptionCompletionRate(completionRate);

        setIsLoading(false);
      } else {
        toast.error(response?.data?.message);
        setIsLoading(false);
      }
    } catch (error) {
      toast.error("Sorry, Something went wrong");
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchRedemptions();
  }, []);

  const statsData = [
    {
      title: "Total Redemptions",
      value: totalRedemptions,
      icon: Tag,
      change: "+12.5%",
      trend: "up",
      bgGradient: "from-violet-500/10 via-purple-500/10 to-fuchsia-500/10",
      iconColor: "text-violet-600",
      borderGradient: "from-violet-500/20 via-purple-500/20 to-fuchsia-500/20",
      textGradient: "from-violet-600 to-fuchsia-600",
      iconBg: "bg-violet-50",
      glowColor: "violet",
    },
    {
      title: "Pending Redemptions",
      value: totalPendingRedemptions,
      icon: Users,
      change: "+5.2%",
      trend: "up",
      bgGradient: "from-amber-500/10 via-orange-500/10 to-yellow-500/10",
      iconColor: "text-amber-600",
      borderGradient: "from-amber-500/20 via-orange-500/20 to-yellow-500/20",
      textGradient: "from-amber-600 to-yellow-600",
      iconBg: "bg-amber-50",
      glowColor: "amber",
    },
    {
      title: "Redemption Completion Rate",
      value: `${totalRedemptionCompletionRate}%`,
      icon: BarChart3,
      change: "+3.1%",
      trend: "up",
      bgGradient: "from-emerald-500/10 via-teal-500/10 to-green-500/10",
      iconColor: "text-emerald-600",
      borderGradient: "from-emerald-500/20 via-teal-500/20 to-green-500/20",
      textGradient: "from-emerald-600 to-green-600",
      iconBg: "bg-emerald-50",
      glowColor: "emerald",
    },
  ];
  // Get unique deals for filter (simplified)
  const uniqueDeals = React.useMemo(() => {
    const deals = redemptionData.map((item) => item.title);
    return ["all", ...new Set(deals)];
  }, [redemptionData]);

  // Add the debounced search handler right after your state declarations
  const debouncedSearch = React.useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Update the filteredAndSortedData memo with proper type checking
  const filteredAndSortedData = React.useMemo(() => {
    let filteredItems = redemptionData;

    // Apply deal filter
    if (selectedDeal !== "all") {
      filteredItems = filteredItems.filter(
        (item) => item.title === selectedDeal
      );
    }

    if (searchTerm.trim()) {
      const searchTerms = searchTerm.toLowerCase().trim().split(" ");
      filteredItems = filteredItems.filter((item) => {
        const searchableFields = {
          customer: String(item.msisdn || "").toLowerCase(),
          deal: String(item.title || "").toLowerCase(),
          type: String(item.redemption_type || "").toLowerCase(),
          code: String(item.redemption_code || "").toLowerCase(),
          status: String(item.status || "").toLowerCase(),
        };
        return searchTerms.every((term) =>
          Object.values(searchableFields).some((field) => field.includes(term))
        );
      });
    }

    // Apply sorting with type checking
    if (sortConfig.key !== null) {
      filteredItems.sort((a, b) => {
        const keyMap = {
          customerName: "msisdn",
          dealTitle: "title",
          code: "redemption_code",
          type: "redemption_type",
          status: "status",
          date: "updated_at",
        };

        const actualKey = keyMap[sortConfig.key] || sortConfig.key;

        const valueA = String(a[actualKey] || "").toLowerCase();
        const valueB = String(b[actualKey] || "").toLowerCase();

        if (valueA < valueB) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredItems;
  }, [redemptionData, searchTerm, sortConfig, selectedDeal]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

  // Generate page numbers
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Updated TypeBadge component
  const TypeBadge = ({ type }) => {
    const isBoth = type.includes("Online") && type.includes("In-Store");
    const isInStore = type.includes("In-Store");

    return (
      <div className="flex flex-col gap-2">
        <div
          className={`
          inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
          ${
            isBoth
              ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600/20"
              : isInStore
              ? "bg-purple-50 text-purple-700 ring-1 ring-purple-600/20"
              : "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20"
          }
        `}
        >
          {isBoth ? (
            <>
              <Store className="w-3.5 h-3.5" />
              <Globe className="w-3.5 h-3.5" />
            </>
          ) : isInStore ? (
            <Store className="w-3.5 h-3.5" />
          ) : (
            <Globe className="w-3.5 h-3.5" />
          )}
          {type.length === 1 ? type : "Both (Online & In-store)"}
        </div>
        <div className="text-[10px] font-medium text-gray-500 flex items-center gap-1">
          {isBoth ? (
            <>
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
              Available both online & in physical stores
            </>
          ) : isInStore ? (
            <>
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
              Available at physical stores
            </>
          ) : (
            <>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              Redeemable online only
            </>
          )}
        </div>
      </div>
    );
  };

  // Table headers configuration
  const tableHeaders = [
    { key: "customerName", label: "Customer Phone" },
    { key: "dealTitle", label: "Campaign Title" },
    { key: "code", label: "Code" },
    { key: "type", label: "Type" },
    { key: "date", label: "Date" },
    { key: "status", label: "Status" },
    { key: "action", label: "Action" },
  ];

  // Updated RedemptionCode component
  const RedemptionCode = ({ code }) => {
    return (
      <div className="inline-flex gap-1.5 items-center px-3 py-1.5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
        <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
          Code
        </span>
        <span className="font-mono text-sm font-medium text-gray-700">
          {code}
        </span>
      </div>
    );
  };

  // Group deals by category
  const groupedDeals = React.useMemo(() => {
    const groups = {};
    redemptionData.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = new Set();
      }
      groups[item.category].add(item.dealTitle);
    });
    return groups;
  }, [redemptionData]);

  // Filter deals based on search term
  const filteredGroupedDeals = React.useMemo(() => {
    if (!filterSearchTerm) return groupedDeals;

    const filtered = {};
    const searchTerm = filterSearchTerm.toLowerCase();

    Object.entries(groupedDeals).forEach(([category, deals]) => {
      const filteredDeals = Array.from(deals).filter(
        (deal) =>
          deal.toLowerCase().includes(searchTerm) ||
          category.toLowerCase().includes(searchTerm)
      );

      if (filteredDeals.length > 0) {
        filtered[category] = new Set(filteredDeals);
      }
    });

    return filtered;
  }, [groupedDeals, filterSearchTerm]);

  // Updated FilterSection component with animations
  const FilterSection = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-6 bg-white rounded-lg shadow"
    >
      <div className="p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex justify-between items-center mb-4"
        >
          <div className="flex gap-2 items-center">
            <motion.div
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
            >
              <Filter className="w-4 h-4 text-gray-500" />
            </motion.div>
            <h3 className="text-sm font-medium text-gray-700">
              Filter by Campaign Title
            </h3>
          </div>
          {selectedDeal !== "all" && (
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSelectedDeal("all")}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear filter
            </motion.button>
          )}
        </motion.div>

        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={handleFilterDropdownClick}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
          >
            <div className="flex gap-2 items-center">
              {selectedDeal === "all" ? (
                "All Campaigns"
              ) : (
                <>
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-2 h-2 rounded-full bg-[#FF6C6C]"
                  />
                  <span className="truncate max-w-[300px]">{selectedDeal}</span>
                </>
              )}
            </div>
            <motion.div
              animate={{ rotate: isFilterDropdownOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </motion.div>
          </motion.button>

          <AnimatePresence>
            {isFilterDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute z-10 mt-2 w-full bg-white rounded-lg border border-gray-200 shadow-lg"
              >
                <div className="max-h-[400px] overflow-y-auto p-2">
                  <motion.button
                    whileHover={{
                      backgroundColor:
                        selectedDeal === "all" ? "#ff8f8f" : "#f3f4f6",
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelectedDeal("all");
                      setIsFilterDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                      selectedDeal === "all"
                        ? "bg-[#FF6C6C] text-white"
                        : "text-gray-700"
                    }`}
                  >
                    All Campaigns
                  </motion.button>

                  {uniqueDeals
                    .filter((deal) => deal !== "all")
                    .map((deal, index) => (
                      <motion.button
                        key={deal}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{
                          backgroundColor:
                            selectedDeal === deal ? "#ff8f8f" : "#f3f4f6",
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedDeal(deal);
                          setIsFilterDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center gap-2 ${
                          selectedDeal === deal
                            ? "bg-[#FF6C6C] text-white"
                            : "text-gray-700"
                        }`}
                      >
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: index * 0.05 + 0.1 }}
                          className="w-1.5 h-1.5 bg-current rounded-full opacity-60"
                        />
                        {deal}
                      </motion.button>
                    ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {selectedDeal !== "all" && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-4"
            >
              <div className="flex gap-2 items-center">
                <span className="text-xs text-gray-500">Active filter:</span>
                <motion.span
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-[#FF6C6C]/10 text-[#FF6C6C]"
                >
                  {selectedDeal}
                </motion.span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  const handleAction = (action, row) => {
    switch (action) {
      case "redeem":
        setModalData(row);
        setIsModalOpen(true);
        break;
      case "remove":
        console.log("Remove:", row);
        break;
      default:
        break;
    }
  };

  const handleModalAction = (actionType, rowData) => {
    console.log("handleModalAction called with:", actionType, rowData);
    setSelectedItem(null); // Reset first
    setTimeout(() => {
      setSelectedItem(rowData);
      setActiveModal(actionType);
    }, 0);
  };

  const calculateDropupPosition = (itemId) => {
    const tableRect = document.querySelector("table").getBoundingClientRect();
    const buttonRect = document
      .querySelector(`#action-${itemId}`)
      ?.getBoundingClientRect();

    if (!buttonRect) return {};

    const distanceFromTop = buttonRect.top - tableRect.top;
    const shouldDropDown = distanceFromTop < 200; // Threshold for switching to dropdown

    return {
      style: shouldDropDown
        ? {
            top: "100%",
            bottom: "auto",
            marginTop: "0.5rem",
            maxHeight: `${window.innerHeight - buttonRect.bottom - 20}px`,
          }
        : {
            bottom: "100%",
            top: "auto",
            marginBottom: "0.5rem",
            maxHeight: `${buttonRect.top - 20}px`,
          },
    };
  };

  const handleFilterDropdownClick = (e) => {
    e.stopPropagation();
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  };

  const RedemptionModal = ({ isOpen, onClose, data }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirmRedemption = async () => {
      setIsSubmitting(true);
      try {
        const response = await campaignsRequest.completeRedemption(data.blast_slug);
        if (response?.data?.status_code === "000" && !response?.data?.in_error) {
          toast.success("Redemption completed successfully");
          await fetchRedemptions();
          onClose();
        } else {
          toast.error(response?.message || "Failed to complete redemption");
        }
      } catch (error) {
        toast.error("Sorry, something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    };

    if (!isOpen || !data) return null;

    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-50 backdrop-blur-sm bg-black/40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="flex overflow-auto fixed inset-0 z-50 justify-center items-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative my-8 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                <div className="sticky top-0 z-10 px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
                  <div className="flex gap-3 items-center">
                    <div className="h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full" />
                    <h3 className="text-xl font-semibold text-gray-800">
                      Mark as Redeemed
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
                  <div className="space-y-6">
                    <div className="inline-flex px-3 py-1 text-sm font-medium text-yellow-700 bg-yellow-50 rounded-full border border-yellow-100">
                      Pending Redemption
                    </div>

                    <div className="grid gap-6">
                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <User className="w-4 h-4 text-[#FF6C6C]" />
                          <span className="text-sm font-medium text-gray-700">
                            Customer
                          </span>
                        </div>
                        <p className="ml-7 font-medium text-gray-900">
                          {data.msisdn}
                        </p>
                      </div>

                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <Tag className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Campaign
                          </span>
                        </div>
                        <p className="ml-7 font-medium text-gray-900">
                          {data.title}
                        </p>
                      </div>

                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <Key className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Redemption Code
                          </span>
                        </div>
                        <p className="ml-7 font-mono font-medium text-gray-900">
                          {data.redemption_code}
                        </p>
                      </div>

                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                        <div className="flex gap-3 items-center mb-1">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">
                            Date
                          </span>
                        </div>
                        <p className="ml-7 font-medium text-gray-900">
                          {moment(data.updated_at).format(
                            "DD MMM YYYY, hh:mm A"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmRedemption}
                      disabled={isSubmitting}
                      className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Processing..." : "Confirm"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  const RedemptionTypeModal = ({ isOpen, onClose, item, type }) => {
    const [selectedLocation, setSelectedLocation] = useState("");

    const locations =
      type === "Online"
        ? [
            { id: "website", label: "Website" },
            { id: "mobile_app", label: "Mobile App" },
            { id: "marketplace", label: "Marketplace" },
          ]
        : [
            { id: "store_1", label: "Store Location 1" },
            { id: "store_2", label: "Store Location 2" },
            { id: "store_3", label: "Store Location 3" },
          ];

    const handleSubmit = () => {
      console.log(`Setting redemption ${type}:`, { item, selectedLocation });
      onClose();
    };

    if (!isOpen || !item) return null;

    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 z-50 backdrop-blur-sm bg-black/40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="flex overflow-auto fixed inset-0 z-50 justify-center items-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative my-8 w-full max-w-lg bg-white rounded-2xl shadow-2xl">
                <div className="sticky top-0 z-10 px-6 py-4 bg-white rounded-t-2xl border-b border-gray-100">
                  <div className="flex gap-3 items-center">
                    <div className="h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full" />
                    <h3 className="text-xl font-semibold text-gray-800">
                      {type === "Online" ? "Online" : "In-Store"} Redemption
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
                  <div className="space-y-6">
                    <div className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center">
                          <span className="w-24 text-gray-500">Deal</span>
                          <span className="font-medium text-gray-900">
                            {item?.title}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-24 text-gray-500">Code</span>
                          <span className="font-mono font-medium text-gray-900">
                            {item?.redemption_code}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Select{" "}
                        {type === "Online" ? "Platform" : "Store Location"}
                      </label>
                      <select
                        value={selectedLocation}
                        onChange={(e) => setSelectedLocation(e.target.value)}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors"
                      >
                        <option value="">Select an option</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 px-6 py-4 bg-white rounded-b-2xl border-t border-gray-100">
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={onClose}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedLocation}
                      className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  const getDropdownPosition = (buttonElement) => {
    if (!buttonElement) return {};

    const rect = buttonElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showBelow = spaceBelow >= 200 || spaceBelow > spaceAbove;

    // Calculate right alignment
    const menuWidth = 180; // Width of the dropdown menu
    const rightSpace = window.innerWidth - rect.left;
    const shouldAlignLeft = rightSpace < menuWidth;

    return {
      position: "fixed",
      ...(shouldAlignLeft
        ? { right: `${window.innerWidth - rect.right}px` }
        : { left: `${rect.left}px` }),
      ...(showBelow
        ? { top: `${rect.bottom + 8}px` }
        : { bottom: `${window.innerHeight - rect.top + 8}px` }),
    };
  };

  // Add this console log to see what data we're getting
  useEffect(() => {
    console.log("Current Items:", currentItems);
  }, [currentItems]);

  // Add this to track selectedItem changes
  useEffect(() => {
    console.log("Selected Item Changed:", selectedItem);
  }, [selectedItem]);

  return (
    <div>
      <Navbar page="Campaign Redemption" />
      <div className="p-6 min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden relative mb-8"
        >
          <div className="flex relative flex-col p-8 bg-white rounded-2xl shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FF6C6C]/10 to-purple-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr rounded-full blur-2xl from-blue-500/10 to-yellow-500/10"></div>

            <div className="relative z-10">
              <div className="flex gap-3 items-center mb-2">
                <div className="h-8 w-1 bg-gradient-to-b from-[#FF6C6C] to-purple-600 rounded-full"></div>
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700"
                >
                  Campaign Redemption
                </motion.h1>
              </div>
              <p className="mt-2 max-w-xl text-gray-600">
                Track and manage customer redemptions with real-time insights
                and detailed analytics
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex gap-3 mt-4 md:mt-0"
            >
              <button className="inline-flex items-center px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] rounded-xl hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg">
                <Download className="mr-2 w-4 h-4" />
                Export Data
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 gap-6 mt-8 md:grid-cols-3"
          >
            {statsData.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className={`
                  relative overflow-hidden p-6 bg-white rounded-2xl
                  before:absolute before:inset-0 before:bg-gradient-to-br ${stat.bgGradient}
                  after:absolute after:inset-px after:rounded-[15px] after:bg-gradient-to-br ${stat.borderGradient}
                  shadow-sm hover:shadow-md transition-all duration-300
                  group hover:scale-[1.02] hover:-translate-y-0.5
                `}
              >
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-500 transition-colors group-hover:text-gray-700">
                        {stat.title}
                      </p>
                      <h3 className={`text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${stat.textGradient}`}>
                        {stat.value}
                      </h3>
                      <div className="flex gap-1.5 items-center">
                        <div className={`h-1.5 w-1.5 rounded-full bg-${stat.glowColor}-500 animate-pulse`}></div>
                        <span className={`text-xs font-medium text-${stat.glowColor}-600`}>
                          {stat.change} from last month
                        </span>
                      </div>
                    </div>
                    <div
                      className={`
                        p-3 rounded-xl bg-gradient-to-br ${stat.bgGradient}
                        border border-gradient-to-br ${stat.borderGradient}
                        group-hover:scale-110 transition-transform duration-300
                        shadow-lg shadow-${stat.glowColor}-500/20
                      `}
                    >
                      <stat.icon className={`w-6 h-6 ${stat.iconColor} transform group-hover:rotate-12 transition-transform`} />
                    </div>
                  </div>

                  <div className="absolute -right-6 -bottom-6 opacity-[0.07] transform rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-transform duration-300">
                    <stat.icon className={`w-32 h-32 ${stat.iconColor}`} />
                  </div>
                </div>
                
                {/* Add subtle glow effect */}
                <div className={`absolute -inset-px bg-gradient-to-r ${stat.bgGradient} opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300`} />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <FilterSection />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="p-4 mb-4 bg-white rounded-lg shadow"
        >
          <div className="relative w-full max-w-md">
            <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors"
              placeholder="Search by campaign title, code or status..."
              defaultValue={searchTerm}
              onChange={(e) => debouncedSearch(e.target.value)}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="overflow-x-auto bg-white rounded-lg shadow"
        >
          <table className="w-full whitespace-nowrap">
            <thead className="bg-gray-50">
              <tr>
                {tableHeaders.map((header) => (
                  <th
                    key={header.key}
                    onClick={() =>
                      header.key !== "action" && handleSort(header.key)
                    }
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex gap-2 items-center whitespace-nowrap">
                      {header.label}
                      {header.key !== "action" && (
                        <ArrowUpDown
                          className={`w-4 h-4 flex-shrink-0 transition-transform ${
                            sortConfig.key === header.key
                              ? sortConfig.direction === "asc"
                                ? "text-[#FF6C6C]"
                                : "text-[#FF6C6C] rotate-180"
                              : ""
                          }`}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.map((rowItem, index) => (
                <tr
                  key={rowItem.redemption_id || index}
                  className="transition-colors hover:bg-gray-50/50"
                >
                  <td className="px-6 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {rowItem.msisdn}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="max-w-xs text-sm text-gray-600 truncate">
                      {rowItem.title}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <RedemptionCode code={rowItem.redemption_code} />
                  </td>
                  <td className="px-6 py-3">
                    <TypeBadge type={rowItem.redemption_type} />
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm text-gray-600">
                      {moment(rowItem.created_at).format("DD-MMM-YYYY")}
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={`
                      px-2.5 py-1 text-xs font-medium rounded-full
                      ${
                        rowItem.status === "completed"
                          ? "bg-green-50 text-green-700 ring-1 ring-green-600/20"
                          : rowItem.status === "pending"
                          ? "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20"
                          : "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20"
                      }
                    `}
                    >
                      {rowItem.status === "pending"
                        ? "Pending Redemption"
                        : rowItem.status === "completed"
                        ? "Redeemed"
                        : "In Progress"}
                    </span>
                  </td>
                  <td className="relative px-6 py-3">
                    <ActionMenu row={rowItem} onAction={handleAction} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex items-center text-sm text-gray-500">
              <span>
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, filteredAndSortedData.length)} of{" "}
                {filteredAndSortedData.length} results
              </span>
            </div>

            <div className="flex gap-2 items-center">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`
                  inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg
                  ${
                    currentPage === 1
                      ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                      : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                  }
                `}
              >
                Previous
              </button>

              <div className="flex gap-1 items-center">
                {pageNumbers.map((number) => (
                  <button
                    key={number}
                    onClick={() => setCurrentPage(number)}
                    className={`
                      inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-lg
                      ${
                        currentPage === number
                          ? "text-white bg-[#FF6C6C]"
                          : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                      }
                    `}
                  >
                    {number}
                  </button>
                ))}
              </div>

              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className={`
                  inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg
                  ${
                    currentPage === totalPages
                      ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                      : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200"
                  }
                `}
              >
                Next
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <RedemptionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalData(null);
        }}
        data={modalData}
      />

      <RedemptionTypeModal
        isOpen={activeModal === "Online"}
        onClose={() => {
          setActiveModal(null);
          setSelectedItem(null);
        }}
        item={selectedItem}
        type="Online"
      />

      <RedemptionTypeModal
        isOpen={activeModal === "in-Store"}
        onClose={() => {
          setActiveModal(null);
          setSelectedItem(null);
        }}
        item={selectedItem}
        type="in-Store"
      />
    </div>
  );
};

const ActionMenu = ({ row, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        className="p-1.5 rounded-lg hover:bg-gray-100"
        onClick={() => setIsOpen(!isOpen)}
      >
        <MoreVertical size={16} className="text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg">
          {row.status === "pending" && (
            <button
              className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left hover:bg-gray-50"
              onClick={() => {
                onAction("redeem", row);
                setIsOpen(false);
              }}
            >
              <CheckCircle2 size={16} className="text-green-600" />
              Mark as Redeemed
            </button>
          )}

          <div className="border-t border-gray-100" />

          <button
            className="flex gap-2 items-center px-4 py-2 w-full text-sm text-left hover:bg-gray-50"
            onClick={() => {
              onAction("remove", row);
              setIsOpen(false);
            }}
          >
            <Trash2 size={16} className="text-red-600" />
            Remove Entry
          </button>
        </div>
      )}
    </div>
  );
};

export default CampaignRedemptions;
